/**
 * @file SlotGroup.tsx
 * @description Место для секций в структуре экрана магазина (phase19.16 Р2
 * — экран «Блог»; Р2а — экран «Каталог», uiux-design-r2a §2, §6): рамка с
 * названием места, его блоки (только просмотр — блок открывается на
 * странице места) и [+ Добавить блок]; пустое место — подсказка, что туда
 * поставить. Страница места создаётся сервером при первом обращении.
 */

import { SITE_SLOTS, type SiteSlotKey } from "@chaiberi/shared";
import { Plus } from "lucide-react";

import { Button } from "../../ui/button";
import { useNotify } from "../../ui/NotifyProvider";
import { useBuilder } from "../builder-context";
import { BlockList } from "./BlockList";
import type { ScreenSlotsApi } from "../../../hooks/site-builder/useScreenSlots";
import { blockErrorText } from "../../../lib/site-builder/block-errors";

interface Props {
  slotKey: SiteSlotKey;
  api: ScreenSlotsApi;
  /** Подсказка пустого места («Пусто — добавьте акции…»); нет — без неё. */
  emptyText?: string;
}

export function SlotGroup({ slotKey, api, emptyText }: Props) {
  const b = useBuilder();
  const notify = useNotify();
  const info = SITE_SLOTS.find((s) => s.key === slotKey);
  const slot = api.slots?.find((s) => s.slot === slotKey);

  async function openSlot(blockId?: string) {
    try {
      const pageId = await api.open(slotKey);
      // Переход мог отменить человек (несохранённый блок) — тогда ничего не открываем.
      const moved = await b.select({ pageId, screen: null, panel: null, sel: blockId ? { kind: "block", id: blockId } : { kind: "page" } });
      if (moved && !blockId) b.openLibrary(undefined);
    } catch (err) {
      notify.error(blockErrorText(err, "Не удалось открыть место для блоков."));
    }
  }

  const empty = !slot || slot.blocks.length === 0;
  return (
    <div className="flex flex-col gap-1 rounded-lg p-1 [box-shadow:inset_0_0_0_1px_var(--outline-variant)]">
      <span className="type-caption px-2 pt-1 text-on-surface-variant">{info?.label}</span>
      {!empty ? <BlockList blocks={slot.blocks} selectedId={null} editable={false} onSelect={(block) => void openSlot(block.id)} /> : null}
      {empty && emptyText && api.slots ? <p className="type-caption px-2 text-on-surface-variant">{emptyText}</p> : null}
      {b.canEditPages ? (
        <Button variant="ghost" className="justify-start text-primary" onClick={() => void openSlot()}>
          <Plus aria-hidden />
          Добавить блок
        </Button>
      ) : null}
    </div>
  );
}
